/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
import { useIsFetching } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { DataRouter } from 'react-router-dom';

import { logger } from '@/lib/logger';

import { sendViewHealthBeacon } from './beacon';
import { VIEW_HEALTH_DEBOUNCE_MS, VIEW_HEALTH_TIMEOUT_MS } from './config';
import type {
  RouteHealthHandle,
  ScopeState,
  ViewHealthContextValue,
} from './types';

// Default context value used when no `ViewHealthProvider` is mounted. All
// operations are no-ops so the markers / scope hooks can be used safely in
// tests and in any code path that runs outside the provider (storybook,
// isolated component tests, etc.) without throwing. In production the App
// root always wraps everything in `ViewHealthProvider`, so this default is
// purely a fall-back for unmounted contexts.
const noopViewHealthContextValue: ViewHealthContextValue = {
  startScope: () => {},
  endScope: () => {},
  markReceived: () => {},
  updateScopeOptions: () => {},
  currentRoutePattern: '',
  isRouteMonitored: false,
  routeScopeVersion: 0,
};

export const ViewHealthContext = createContext<ViewHealthContextValue>(
  noopViewHealthContextValue
);

function extractRoutePattern(
  matches: Array<{ route: { path?: string; handle?: RouteHealthHandle } }>
): string {
  try {
    // React Router matches often include a root layout with `path: '/'`.
    // Strip leading slashes per segment THEN drop empties so the root `/`
    // doesn't contribute a phantom segment that joins into `//` (e.g.
    // `['/', '/:orgId', 'crm/contacts']` must produce `/:orgId/crm/contacts`,
    // not `//:orgId/crm/contacts`). The latter gets rejected by the backend
    // allowlist and silently drops the beacon.
    const segments = matches
      .map(m => m.route.path)
      .filter((p): p is string => p != null)
      .map(p => p.replace(/^\/+/, ''))
      .filter(p => p !== '');
    const pattern = '/' + segments.join('/');
    return pattern === '/' && matches.length > 0 ? '/' : pattern;
  } catch {
    return '/';
  }
}

export function ViewHealthProvider({
  router,
  children,
}: {
  router: DataRouter;
  children: ReactNode;
}) {
  const scopesRef = useRef(new Map<string, ScopeState>());
  const [currentRoutePattern, setCurrentRoutePattern] = useState('');
  const [isRouteMonitored, setIsRouteMonitored] = useState(false);
  // Bumps every time a route scope is (re-)created — including cases where
  // the pattern string is unchanged (e.g. in-page navigation within a
  // `actions/*` wildcard). This is what lets `useViewHealthMarker` re-fire
  // its marker for the new scope when its host component stays mounted
  // across the navigation. See ViewHealthContextValue.routeScopeVersion.
  const [routeScopeVersion, setRouteScopeVersion] = useState(0);
  const fetchingCountRef = useRef(0);
  const prevPathnameRef = useRef<string>('');
  const settlementDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const fetchingCount = useIsFetching();

  /** Start a 150ms debounced settlement check. Settles all unsettled route
   *  scopes whose markers are satisfied. Only accesses refs — stable across
   *  renders. */
  const startSettlementDebounce = useCallback(() => {
    if (settlementDebounceRef.current) {
      clearTimeout(settlementDebounceRef.current);
    }
    settlementDebounceRef.current = setTimeout(() => {
      for (const scope of scopesRef.current.values()) {
        if (!scope.settled) {
          // If no variants declared (overlays), settle immediately with no
          // matched-variant label. For routes with variants, pick the MOST
          // SPECIFIC satisfied variant — the one with the most required
          // markers. This means richer variants (e.g. PROPOSAL_LOADED with 4
          // markers) win over their less-specific supersets (e.g. LOADED
          // with 2 markers) when all are simultaneously satisfied, which is
          // the common case on an `actions/*` page that settles with a
          // proposal open. Ties (equal marker counts) are broken by
          // insertion order, so authors can still influence resolution for
          // same-size mutually-exclusive variants (e.g. LOADED vs EMPTY).
          const hasVariants = Object.keys(scope.successVariants).length > 0;
          let matchedVariant: string | null = null;
          if (hasVariants) {
            let matchedMarkerCount = -1;
            for (const [name, variantMarkers] of Object.entries(
              scope.successVariants
            )) {
              const satisfied = [...variantMarkers].every(m =>
                scope.receivedMarkers.has(m)
              );
              if (satisfied && variantMarkers.size > matchedMarkerCount) {
                matchedVariant = name;
                matchedMarkerCount = variantMarkers.size;
              }
            }
          }
          const anyVariantSatisfied = !hasVariants || matchedVariant !== null;
          if (anyVariantSatisfied) {
            // Prefer matched variant from declared variants (routes). Fall back to
            // the caller-supplied override (overlays). Either is fine to omit.
            const finalVariant =
              matchedVariant ?? scope.successVariantOverride ?? null;
            sendViewHealthBeacon({
              scope_type: scope.scopeType,
              scope_name: scope.scopeName,
              outcome: 'success',
              duration_ms: Math.round(performance.now() - scope.startTime),
              ...(finalVariant !== null && { success_variant: finalVariant }),
              ...(scope.entityType !== undefined && {
                entity_type: scope.entityType,
              }),
            });
            scope.settled = true;
            if (scope.timeoutId) {
              clearTimeout(scope.timeoutId);
              scope.timeoutId = null;
            }
          }
        }
      }
      settlementDebounceRef.current = null;
    }, VIEW_HEALTH_DEBOUNCE_MS);
  }, []);

  const sendTimeoutBeacon = useCallback((scopeId: string): void => {
    const scope = scopesRef.current.get(scopeId);
    if (!scope || scope.settled) {
      return;
    }
    scope.settled = true;
    // Compute markers_missing across ALL variants flattened and deduplicated.
    const allRequiredMarkers = new Set(
      Object.values(scope.successVariants).flatMap(s => [...s])
    );
    const missingMarkers = [...allRequiredMarkers].filter(
      m => !scope.receivedMarkers.has(m)
    );
    // Succeeded markers = received markers that are actually expected by some
    // variant (filters out stray markers fired from other scopes/components).
    const succeededMarkers = [...scope.receivedMarkers].filter(m =>
      allRequiredMarkers.has(m)
    );
    // Debug log so manual testing shows exactly which markers fired and which
    // didn't when a route times out — easier to triage slow loads than the
    // beacon payload alone.
    logger.debug('View health timer timed out', {
      scope_type: scope.scopeType,
      scope_name: scope.scopeName,
      duration_ms: Math.round(performance.now() - scope.startTime),
      markers_succeeded: succeededMarkers,
      markers_missing: missingMarkers,
    });
    sendViewHealthBeacon({
      scope_type: scope.scopeType,
      scope_name: scope.scopeName,
      outcome: 'timeout',
      duration_ms: Math.round(performance.now() - scope.startTime),
      error_type: 'timeout',
      markers_missing: missingMarkers,
      ...(scope.entityType !== undefined && { entity_type: scope.entityType }),
    });
  }, []);

  const createRouteScope = useCallback(
    (
      matches: Array<{
        route: { path?: string; handle?: RouteHealthHandle };
        params?: Record<string, string | undefined>;
      }>
    ): void => {
      const rawPattern = extractRoutePattern(matches);
      const pattern = rawPattern === '' ? '/' : rawPattern;
      // Always update pattern — useViewHealthMarker uses it to derive scope ids,
      // and it's cheap to keep in sync even on unmonitored routes.
      setCurrentRoutePattern(pattern);
      // Bump the version every time this runs, even when the pattern is
      // unchanged. This is how persistently-mounted components (wildcard
      // routes) learn that their scope was recreated and that they need to
      // re-fire markers. React bails out of `setState` calls that don't
      // change the value, so we MUST pass a functional update to guarantee
      // an actual state change.
      setRouteScopeVersion(v => v + 1);

      const handleMatch = [...matches]
        .reverse()
        .find(m => m.route.handle?.health != null);
      const health = handleMatch?.route.handle?.health;

      // Cancel any existing route scope (abandoned navigation — no beacon).
      // Run this for monitored AND unmonitored destinations so a prior
      // monitored scope doesn't leak into a new route.
      for (const [scopeId, scope] of scopesRef.current.entries()) {
        if (scope.scopeType === 'route') {
          if (scope.timeoutId) {
            clearTimeout(scope.timeoutId);
          }
          if (scope.debounceId) {
            clearTimeout(scope.debounceId);
          }
          scopesRef.current.delete(scopeId);
        }
      }

      // Clear any pending settlement debounce from the previous route
      if (settlementDebounceRef.current) {
        clearTimeout(settlementDebounceRef.current);
        settlementDebounceRef.current = null;
      }

      // Gate: only routes with an explicit `handle.health` are reported.
      // Unmonitored routes have no reliable "ready" signal — React Query
      // settlement alone doesn't distinguish "page rendered" from "page
      // actually usable" — so we skip them to keep Grafana signal/noise
      // and cardinality low.
      if (!health) {
        setIsRouteMonitored(false);
        return;
      }

      // Defense-in-depth: warn if success variants are empty (TS should have
      // caught this, but guard against dynamic config or future regressions).
      // Keep `isRouteMonitored` false here because no scope is created below.
      if (Object.keys(health.success).length === 0) {
        setIsRouteMonitored(false);
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            '[view-health] Route has health config but no success variants — skipping monitoring.',
            { pattern }
          );
        }
        return;
      }
      setIsRouteMonitored(true);

      const scopeId = `route:${pattern}`;
      // scopeName may be overridden by a resolver that includes URL params
      // (e.g. section-level granularity). scopeId stays pattern-based to avoid
      // duplicate scopes when navigating between sections of the same route.
      const rawParams = matches[matches.length - 1]?.params ?? {};
      // React Router params may include undefined values (optional segments).
      // Filter to only string values before passing to the resolver.
      const lastMatchParams: Record<string, string> = Object.fromEntries(
        Object.entries(rawParams).filter(
          (entry): entry is [string, string] => typeof entry[1] === 'string'
        )
      );
      const resolvedScopeName = health.scopeName
        ? health.scopeName(lastMatchParams)
        : pattern;
      const successVariants: Record<string, Set<string>> = {};
      for (const [name, markers] of Object.entries(health.success)) {
        successVariants[name] = new Set(markers);
      }
      const scope: ScopeState = {
        id: scopeId,
        scopeType: 'route',
        scopeName: resolvedScopeName,
        startTime: performance.now(),
        timeoutId: setTimeout(() => sendTimeoutBeacon(scopeId), health.timeout),
        debounceId: null,
        successVariants,
        receivedMarkers: new Set(),
        failureMarkers: new Set(health.failure ?? []),
        settled: false,
      };
      scopesRef.current.set(scopeId, scope);

      // Debug log gated by window.__ahoy.enable() — mirrors the beacon log
      // so manual testing shows both ends of a scope: when the timer starts
      // and when the beacon fires.
      logger.debug('View health route timer started', {
        scope_type: 'route',
        scope_name: resolvedScopeName,
        timeout_ms: health.timeout,
        success_variants: Object.keys(health.success),
        failure_markers: health.failure ?? [],
      });

      // If no queries are in-flight, the settlement useEffect won't re-fire
      // (fetchingCount is already 0 — no change to trigger it). Kick off the
      // 150ms settlement debounce immediately so routes without pending fetches
      // settle promptly instead of timing out after 30s.
      if (fetchingCountRef.current === 0) {
        startSettlementDebounce();
      }
    },
    [startSettlementDebounce, sendTimeoutBeacon]
  );

  // Router subscription: initial load + location-change navigations.
  //
  // We watch `state.location.pathname` rather than React Router's
  // navigation state because Ahoy's routes use TanStack Query for data
  // fetching, not route `loader` functions. Without loaders, navigations
  // go `idle → idle` with no "loading" phase — so a `prev === 'loading'`
  // check would never match and scopes would never start post-initial-load.
  // Pathname-change detection works for both loader and loader-less routes.
  useEffect(() => {
    // Capture ref value for cleanup (react-hooks/exhaustive-deps requirement)
    const scopes = scopesRef.current;
    // Handle initial page load (router starts in idle; only pathname is
    // meaningful at this point)
    prevPathnameRef.current = router.state.location?.pathname ?? '';
    createRouteScope(router.state.matches);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const unsubscribe = (router as any).subscribe(
      (state: {
        navigation: { state: string };
        matches: Array<{
          route: { path?: string; handle?: RouteHealthHandle };
          params?: Record<string, string | undefined>;
        }>;
        location: { pathname?: string };
      }) => {
        const newPathname = state.location?.pathname;
        if (newPathname && newPathname !== prevPathnameRef.current) {
          prevPathnameRef.current = newPathname;
          createRouteScope(state.matches);
        }
      }
    );

    return () => {
      unsubscribe();
      for (const scope of scopes.values()) {
        if (scope.timeoutId) {
          clearTimeout(scope.timeoutId);
        }
        if (scope.debounceId) {
          clearTimeout(scope.debounceId);
        }
      }
      scopes.clear();
      if (settlementDebounceRef.current) {
        clearTimeout(settlementDebounceRef.current);
      }
    };
  }, [router, createRouteScope]);

  // Settlement detection: watch useIsFetching
  useEffect(() => {
    fetchingCountRef.current = fetchingCount;

    if (fetchingCount === 0) {
      // All queries settled — start debounce
      startSettlementDebounce();
    } else {
      // A query is in flight — clear any pending settlement debounce
      if (settlementDebounceRef.current) {
        clearTimeout(settlementDebounceRef.current);
        settlementDebounceRef.current = null;
      }
    }
  }, [fetchingCount, startSettlementDebounce]);

  const startScope = useCallback(
    (
      id: string,
      type: 'route' | 'overlay',
      name: string,
      options?: { entityType?: string; successVariant?: string }
    ) => {
      if (scopesRef.current.has(id)) {
        return;
      }
      const scope: ScopeState = {
        id,
        scopeType: type,
        scopeName: name,
        startTime: performance.now(),
        timeoutId: setTimeout(
          () => sendTimeoutBeacon(id),
          VIEW_HEALTH_TIMEOUT_MS
        ),
        debounceId: null,
        successVariants: {}, // No variants = settles immediately when queries settle
        receivedMarkers: new Set(),
        failureMarkers: new Set(),
        settled: false,
        entityType: options?.entityType,
        successVariantOverride: options?.successVariant,
      };
      scopesRef.current.set(id, scope);

      // If no queries are in-flight, kick off settlement debounce immediately
      // (same logic as createRouteScope — without this, the overlay would
      // never settle and always emit a timeout beacon).
      if (fetchingCountRef.current === 0) {
        startSettlementDebounce();
      }
    },
    [startSettlementDebounce, sendTimeoutBeacon]
  );

  const endScope = useCallback((id: string) => {
    const scope = scopesRef.current.get(id);
    if (!scope) {
      return;
    }
    if (scope.timeoutId) {
      clearTimeout(scope.timeoutId);
    }
    if (scope.debounceId) {
      clearTimeout(scope.debounceId);
    }
    scopesRef.current.delete(id);
  }, []);

  const updateScopeOptions = useCallback(
    (id: string, options: { entityType?: string; successVariant?: string }) => {
      const scope = scopesRef.current.get(id);
      if (!scope || scope.settled) {
        return;
      }
      // Only update fields that the caller explicitly provided. Treating an
      // omitted field as `undefined` would clobber a previously set value
      // (e.g. an entity-type update arriving before notes load shouldn't
      // clear a successVariant set on a later render).
      if (options.entityType !== undefined) {
        scope.entityType = options.entityType;
      }
      if (options.successVariant !== undefined) {
        scope.successVariantOverride = options.successVariant;
      }
    },
    []
  );

  const markReceived = useCallback(
    (scopeId: string, markerName: string) => {
      const scope = scopesRef.current.get(scopeId);
      if (!scope) {
        return;
      }

      // Failure markers short-circuit immediately to error outcome — no debounce.
      if (scope.failureMarkers.has(markerName)) {
        if (scope.settled) {
          return;
        }
        scope.settled = true;
        if (scope.timeoutId) {
          clearTimeout(scope.timeoutId);
          scope.timeoutId = null;
        }
        // Do NOT clear the global settlement debounce here — other scopes
        // may be pending settlement and clearing the debounce would starve them.
        sendViewHealthBeacon({
          scope_type: scope.scopeType,
          scope_name: scope.scopeName,
          outcome: 'error',
          duration_ms: Math.round(performance.now() - scope.startTime),
          error_type: markerName,
          ...(scope.entityType !== undefined && {
            entity_type: scope.entityType,
          }),
        });
        return;
      }

      scope.receivedMarkers.add(markerName);
      if (fetchingCountRef.current === 0) {
        startSettlementDebounce();
      }
    },
    [startSettlementDebounce]
  );

  const contextValue = useMemo(
    () => ({
      startScope,
      endScope,
      markReceived,
      updateScopeOptions,
      currentRoutePattern,
      isRouteMonitored,
      routeScopeVersion,
    }),
    [
      startScope,
      endScope,
      markReceived,
      updateScopeOptions,
      currentRoutePattern,
      isRouteMonitored,
      routeScopeVersion,
    ]
  );

  return (
    <ViewHealthContext.Provider value={contextValue}>
      {children}
    </ViewHealthContext.Provider>
  );
}
